// @flow
import type {GraphQLTaggedNode} from 'react-relay'
import {graphql} from 'react-relay'
import {startsWith} from 'underscore.string'
import {pathOr} from 'ramda'

import Editor from './Editor'
import Summary from './Summary'
import createPartComponent from '../createPartComponent'
import type {Strategy} from '../../types'
import type {Change, State} from './types'
import fetchFieldsFromChange from './fetchFieldsFromChange'
import changePayload from './changePayload'

/* eslint class-methods-use-this: 0 */

const query = {
  organization: graphql`
    fragment Policy_organization on Organization {
      databaseId

      ...WYSIWYGTextareaWithFeatureFlag_organization
    }
  `,
  proposal: graphql`
    fragment Policy_proposal on Proposal {
      databaseId
      circle {
        databaseId
      }
      tension {
        body
      }
    }
  `,
  subscriptionChange: graphql`
    fragment PolicySubscription_change on DescribeChangeable {
      command
      databaseId
      id
      type

      alerts {
        path
        message
        severity
      }

      ... on PolicyChange {
        policyHasBeenRemoved

        policy {
          databaseId
          title
          body

          domain {
            databaseId
            description
          }
        }
      }
    }
  `,
  change: graphql`
    fragment Policy_change on DescribeChangeable {
      ... on PolicyChange {
        availableDomains(first: 100) {
          edges {
            node {
              databaseId
              description(format: PLAIN)
            }
          }
        }
      }
      ...SummarySections_change
      ...PolicySubscription_change @relay(mask: false)
    }
  `,
}

const policyTitle = pathOr('', ['policy', 'title'])

class PolicyStrategy implements Strategy {
  static displayName: string = 'Policy'
  static query: {[string]: GraphQLTaggedNode, ...} = query
  static fieldsFromChange: ((change: any) => any) = fetchFieldsFromChange

  change: Change
  changeKey: string
  query: string

  constructor(change: Change) {
    this.change = change
    this.changeKey = `Part-Policy-${change.databaseId || 'new'}`
  }

  get className(): string {
    return 'policy-part'
  }

  get partDescription(): string {
    return policyTitle(this.change)
  }

  get partTitle(): string {
    return startsWith(this.change.type, 'Create')
      ? I18n.t('governance_meetings.react.new_policy')
      : I18n.t('governance_meetings.react.update_policy')
  }

  get error(): ?string {
    if (this.change.policyHasBeenRemoved)
      return I18n.t('proposals.react.policy_part_not_loaded')

    return null
  }

  changePayload(state: State): any {
    return changePayload(this.change, state)
  }
}

export {
  PolicyStrategy,
}

export default (createPartComponent(PolicyStrategy, Editor, Summary): any)
